import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Card, CardContent, Grid, IconButton, InputAdornment, Paper, TextField } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";
import { useNavigate } from "react-router-dom";

import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import SendIcon from "@material-ui/icons/Send";

import { Validation } from "../../../../../../../@types/validation.types";
import { ChangePassword } from "../../../../../../../@types/changePassword.types";
import { SnackbarProps } from "../../../../../../../@types/snackbarAPI.types";
import { api } from "../../../../../../../utils/api";
import validatePassword from "../../../../../../../utils/commonFunctions/validatePassword";
import { DynamicFormField } from "../../../../../../../@types/dynamicForm.types";
import ExtractRouteParams from "../../../../../../../utils/commonFunctions/extractRouteParams";
import useRouteParam from "../../../../../../../hooks/useRouteParam";
import { UIContext } from "../../../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../../../constants/caption/dashboardHeader";
import SnackbarAPICall from "../../../../../../shared/snackbar/SnackbarAPICall";
import { MemberChangePasswordTypes } from "../../../../../../../utils/api/auth/memberChangePassword";

const MemberChangePassword: React.FC<any> = (): JSX.Element => {
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showNewConfirmPassword, setShowNewConfirmPassword] = useState(false);
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [newPassword, setNewPassword] = useState<string>("");
	const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
	const [passwordValidation, setPasswordValidation] = useState<Validation>({
		newPassword: "",
		confirmNewPassword: "",
		status: "invalid"
	});
	const [passwordDetailsField, setPasswordDetailsField] = useState<DynamicFormField[]>([]);
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});
	const navigate = useNavigate();
	const { setDashboardHeader } = useContext(UIContext);
	const querySSN = useRouteParam("qs");
	const queryGroupNumber = useRouteParam("gn");
	const routeParam = ExtractRouteParams();

	const _passwordValidation = useRef<Validation>();

	const handlePasswordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		if (name === "confirm_new_password") setConfirmNewPassword(value);
		else setNewPassword(value);
	}, []);

	const handleRedirectToMemberDetails = useCallback(() => {
		const pathname = `/employer/member?${routeParam}`;
		setTimeout(() => {
			navigate(pathname);
		}, 1500);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePasswordValidation = useCallback(async () => {
		_passwordValidation.current = {
			...passwordValidation,
			newPassword: "",
			confirmNewPassword: "",
			status: "invalid"
		};
		let flag = true,
			confirmPasswordFlag = true,
			validPasswordFlag = true;

		if (newPassword.length === 0) {
			_passwordValidation.current["newPassword"] = "New Password is required";
			_passwordValidation.current["status"] = "invalid";
			flag = false;
		}
		if (newPassword.length !== 0) {
			if (!validatePassword(newPassword)) {
				_passwordValidation.current["newPassword"] = "Please enter a valid password";
				_passwordValidation.current["status"] = "invalid";
				validPasswordFlag = false;
			}
		}
		if (confirmNewPassword.length === 0) {
			_passwordValidation.current["confirmNewPassword"] = "Confirm New Password is required";
			_passwordValidation.current["status"] = "invalid";
			flag = false;
		}
		if (confirmNewPassword !== newPassword) {
			_passwordValidation.current["confirmNewPassword"] = "Must be same as New Password";
			_passwordValidation.current["status"] = "invalid";
			confirmPasswordFlag = false;
		}

		if (flag === true && confirmPasswordFlag === true && validPasswordFlag === true)
			_passwordValidation.current["status"] = "valid";
		else if (confirmPasswordFlag === false) {
			_passwordValidation.current["status"] = "invalid";
			alert("Confirm New Password must be same with New Password!");
		} else if (validPasswordFlag === false) {
			_passwordValidation.current["status"] = "invalid";
			alert(
				"Password length must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one number and one special character!"
			);
		} else {
			_passwordValidation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setPasswordValidation(Object.assign({}, _passwordValidation.current));
		return _passwordValidation.current["status"];
	}, [confirmNewPassword, newPassword, passwordValidation]);

	const handleUpdatePassword = useCallback(async () => {
		try {
			const memberChangePasswordPayload: MemberChangePasswordTypes = {
				group_number: queryGroupNumber as number,
				SSN: querySSN as string,
				new_password: newPassword
			};

			const response = await trackPromise(api.auth.memberChangePassword(memberChangePasswordPayload));

			if (response) {
				setSnackbarAPICallProps(
					Object.assign({}, snackbarAPICallProps, {
						open: true,
						message: "Member Password Changed Successfully!",
						severity: "success"
					})
				);

				setTimeout(() => {
					handleRedirectToMemberDetails();
				}, 2000);
			}
		} catch (err) {
			alert("Failed to update password!");
		}
	}, [handleRedirectToMemberDetails, newPassword, queryGroupNumber, querySSN, snackbarAPICallProps]);

	const handlePasswordSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const passwordValidationInstance = await handlePasswordValidation();
				if (passwordValidationInstance === "valid") {
					// console.log("form is valid");
					await handleUpdatePassword();
				}
			} catch (error) {
				alert("Unable to Change Password!");
				throw error;
			}
		},
		[handlePasswordValidation, handleUpdatePassword]
	);

	const handleShowNewPassword = () => setShowNewPassword(!showNewPassword);
	const handleShowNewConfirmPassword = () => setShowNewConfirmPassword(!showNewConfirmPassword);

	useEffect(() => {
		setPasswordDetailsField(
			Object.assign(
				[],
				[
					{
						label: "New Password",
						name: "new_password",
						value: newPassword,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange(event),
						type: "textfield"
					},
					{
						label: "Confirm New Password",
						name: "confirm_new_password",
						value: confirmNewPassword,
						onChange: (event: React.ChangeEvent<HTMLInputElement>) => handlePasswordChange(event),
						type: "textfield"
					}
				]
			)
		);
	}, [confirmNewPassword, handlePasswordChange, newPassword]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.member_change_password);
	}, [setDashboardHeader]);

	return (
		<div className="member-change-password-container" id="member-change-password-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="member-action-button-container" id="pf-action-button-container">
				<Button className="button-green" onClick={handlePasswordSubmit} variant="contained">
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
			<div className="pf-title">Change Password</div>
			<Paper className="pf-paper-container" elevation={3} variant="outlined">
				<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
					<Grid container spacing={1} className="pf-grid-container">
						{passwordDetailsField.map((data, index) => {
							const { name, label, value, type, onChange } = data;
							return (
								<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
									<Grid container spacing={1} className="pf-label">
										<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
											<div className="pf-label-text" id="pf-label-text">
												{label}
											</div>
										</Grid>
										<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
											{type === "textfield" ? (
												<TextField
													className="input-text-field"
													id="input-text-field"
													type={
														name === "new_password"
															? !showNewPassword
																? "password"
																: "text"
															: name === "confirm_new_password"
															? !showNewConfirmPassword
																? "password"
																: "text"
															: "text"
													}
													value={value}
													helperText={
														name === "new_password"
															? passwordValidation["newPassword"]
															: name === "confirm_new_password"
															? passwordValidation["confirmNewPassword"]
															: ""
													}
													name={name}
													onChange={onChange}
													variant="outlined"
													style={{
														pointerEvents: "unset",
														width: "340px",
														borderRadius: 50
													}}
													InputProps={{
														startAdornment: (
															<InputAdornment position="start">
																<LockIcon style={{ color: "#7cb342" }} />
															</InputAdornment>
														),
														endAdornment: (
															<InputAdornment position="end">
																{name === "new_password" ? (
																	!showNewPassword ? (
																		<IconButton onClick={handleShowNewPassword}>
																			<VisibilityOffIcon
																				style={{ color: "#7cb342" }}
																			/>
																		</IconButton>
																	) : (
																		<IconButton onClick={handleShowNewPassword}>
																			<VisibilityIcon
																				style={{ color: "#7cb342" }}
																			/>
																		</IconButton>
																	)
																) : name === "confirm_new_password" ? (
																	!showNewConfirmPassword ? (
																		<IconButton
																			onClick={handleShowNewConfirmPassword}
																		>
																			<VisibilityOffIcon
																				style={{ color: "#7cb342" }}
																			/>
																		</IconButton>
																	) : (
																		<IconButton
																			onClick={handleShowNewConfirmPassword}
																		>
																			<VisibilityIcon
																				style={{ color: "#7cb342" }}
																			/>
																		</IconButton>
																	)
																) : null}
															</InputAdornment>
														)
													}}
												/>
											) : null}
										</Grid>
									</Grid>
								</Grid>
							);
						})}
					</Grid>
				</Grid>
			</Paper>
		</div>
	);
};

export default MemberChangePassword;
